import React, { useEffect, useMemo, useState } from 'react';
import { IField, IPatient, ISection, ISubmission, ISurvey } from '../../../constants/types';
import { BillingInfo } from '../Billing/types';
import { Button, Grid, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import SurveyRecordsTable from '../SurveyRecordsTable';
import { ButtonBack, TypographyStyled } from '../../../components/general/mini_components';
import { fetchRecordsPatientFromSurveyService } from '../../../services';
import { isSlimSubmission } from '../../../utils/slimSubmissions';


interface SubmissionsTableSelectorProps {
    surveys:ISurvey[],
    locale:string,
    records: ISubmission[],
    billingInfo: BillingInfo | null,
    // SHER-529: the patient (PatientInvestigation uuid) + investigation are needed to fetch a
    // selected survey's FULL submissions on demand — the list this component receives is slim.
    uuidInvestigation?: string,
    patientUuid?: string,
    onSelectSubmission: (index:number) => void;
}

const SubmissionsTableSelector: React.FC<SubmissionsTableSelectorProps> = ({ surveys, locale,
                                        records, billingInfo, uuidInvestigation, patientUuid, onSelectSubmission }) => {
    const surveysWithRecords = useMemo(() => {
        return records.reduce((acc:ISurvey[], current:ISubmission) => {
                const surveyRecord = surveys.find((survey) => {
                    return survey.uuid === current.uuidSurvey
                })
                if(surveyRecord && !acc.find((survey) => survey.uuid === surveyRecord.uuid)){
                    acc.push(surveyRecord)
                }
                return acc;
            }, [])                           
    }, [records])                  
    
    const [selectedSurvey, setSelectedSurvey] = useState<ISurvey | null>(null);
    // SHER-529: FULL submissions (with surveyRecords) fetched on demand for the selected survey.
    // The list handed to this component is slim (SHER-483: surveyRecords stripped), but the nursing
    // grid renders every submission's clinical fields inline, so it needs the full content. Keyed
    // by survey uuid + the slim submission-id signature so a newly saved entry (which changes the
    // slim list) invalidates the cache and is re-fetched — the bug also affected new data.
    const [fullSubmissionsByKey, setFullSubmissionsByKey] = useState<Record<string, ISubmission[]>>({});

    // Slim rows + cache key for the selected survey. Shared by the fetch effect and the render so
    // both look up the exact same cache entry.
    const selectedSlimRecords = useMemo(
        () => selectedSurvey ? records.filter((record) => record.uuidSurvey === selectedSurvey.uuid) : [],
        [records, selectedSurvey]
    );
    const selectedCacheKey = useMemo(
        () => selectedSurvey
            ? `${selectedSurvey.uuid}|${selectedSlimRecords.map((record) => record.id).sort().join(',')}`
            : '',
        [selectedSurvey, selectedSlimRecords]
    );

    useEffect(() => {
        if(surveysWithRecords.length === 1){
            setSelectedSurvey(surveys[0]);
        }
    }, [surveysWithRecords]);

    // SHER-529: when a nursing survey is selected and its rows are slim (no surveyRecords), fetch
    // that ONE survey's full submissions. Bounded (a single survey for one patient), so it restores
    // the clinical data without re-introducing the bulk download SHER-483 removed.
    useEffect(() => {
        if(!selectedSurvey || !uuidInvestigation || !patientUuid){
            return;
        }
        if(fullSubmissionsByKey[selectedCacheKey]){
            return;
        }
        const needsFull = selectedSlimRecords.some((record:any) =>
            isSlimSubmission(record) || !(record.surveyRecords && record.surveyRecords.length));
        if(!needsFull){
            return;
        }
        let cancelled = false;
        fetchRecordsPatientFromSurveyService(uuidInvestigation, patientUuid, selectedSurvey.uuid)
            .then((data:any) => {
                if(cancelled){
                    return;
                }
                const submissions:ISubmission[] = data?.surveys?.[0]?.submissions ?? [];
                setFullSubmissionsByKey((prev) => ({ ...prev, [selectedCacheKey]: submissions }));
            })
            .catch(() => { /* fall back to the slim rows rather than blocking the table */ });
        return () => { cancelled = true; };
    }, [selectedSurvey, uuidInvestigation, patientUuid, selectedCacheKey, selectedSlimRecords, fullSubmissionsByKey]);

    function onSurveySelected(survey:ISurvey){
        setSelectedSurvey(survey);
    }

    function renderSelector(){
        
        return(
            <Grid container xs={12}>
                <Grid item xs={12}>
                    <Typography variant='body2'>
                        <Translate id="hospital.patient.submission-table.selector.title" />
                    </Typography>
                </Grid>
                {
                    surveysWithRecords.sort((a, b) => a.order - b.order).map((survey) => {
                        return (
                            <Grid item xs={12}>
                                <Button onClick={() => onSurveySelected(survey)}>
                                    <TypographyStyled variant='body2'>
                                        {survey.name}
                                    </TypographyStyled>
                                </Button>
                            </Grid>
                        )
                    })
                }
            </Grid>
        ) 
    }                                                                
    if(selectedSurvey){
        // SHER-529: prefer the full submissions (with surveyRecords) once fetched; the slim list
        // only carries metadata, which is what left every clinical column blank. Keep the SLIM
        // order (map full back onto each slim row by id) so the row index the table reports on
        // click still lines up with the parent's slim-ordered list — i.e. navigation behaves
        // exactly as before, only the clinical cells are now populated.
        const fullForSurvey = fullSubmissionsByKey[selectedCacheKey];
        const surveysRecords = fullForSurvey
            ? selectedSlimRecords.map((slim) => fullForSurvey.find((full) => full.id === slim.id) ?? slim)
            : selectedSlimRecords
        if(surveysRecords.length > 0){
            const fieldsSurvey:IField[] = selectedSurvey.sections.reduce((acc:IField[], current:ISection) => {
                const fieldsWithUpdatedOrder = current.fields.map((field:IField) => ({
                    ...field,
                    order: current.order * 1000 + field.order
                }));
                return acc.concat(fieldsWithUpdatedOrder)
            }, [])
            return (
                <Grid container xs={12}>
                    {
                        surveysWithRecords.length > 1 && 
                        <Grid item xs={12}>
                            <ButtonBack onClick={() => setSelectedSurvey(null)}></ButtonBack>
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <SurveyRecordsTable fields={fieldsSurvey} submissions={surveysRecords} 
                            locale={locale} onSelectSubmission={(index) => onSelectSubmission(index)}  
                        />    
                    </Grid>
                </Grid>
            )
        }
        else{
            return <span>No records found</span>;
        }
        
    }
    return renderSelector()
};

export default SubmissionsTableSelector;
